import { ChatConversationSummary, StreamingActivity, StreamingActivityEvent } from '../../interfaces/ChatInterface';

const TOOL_LABELS: Record<string, string> = {
  metrics_catalog: 'Looking up available metrics…',
  query_metrics: 'Querying district metrics…',
  visualizations_catalog: 'Looking up visualizations…',
  query_visualizations: 'Preparing visualization…',
  survey_metrics_catalog: 'Looking up survey metrics…',
  query_survey_metrics: 'Querying survey metrics…',
  get_survey_response_stats: 'Checking survey response counts…',
  search_courses: 'Searching the course catalog…',
  get_artifact_photos: 'Looking up workshop artifact photos…',
};

const toolLabel = (toolName: string): string => TOOL_LABELS[toolName] ?? `Using ${toolName}…`;

const newConversationScope = (base: string): string => `${base}-${crypto.randomUUID()}`;

const filterConversations = (conversations: ChatConversationSummary[], query: string): ChatConversationSummary[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return conversations;
  return conversations.filter((conversation) => conversation.title.toLowerCase().includes(trimmed));
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Recent edits read as relative ("2 days ago"); older ones fall back to a short date ("6/6/26"),
// matching the conversation list design.
const formatLastEdited = (isoTimestamp: string): string => {
  const editedAt = new Date(isoTimestamp);
  if (Number.isNaN(editedAt.getTime())) return '';

  const diffDays = Math.floor((Date.now() - editedAt.getTime()) / MS_PER_DAY);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return editedAt.toLocaleDateString('en-US', { year: '2-digit', month: 'numeric', day: 'numeric' });
};

const INITIAL_STREAMING_ACTIVITY: StreamingActivity = { kind: 'idle' };

// Single source of truth for the live streaming phase. Each event fully replaces the phase, so
// the previously separate states can never drift out of sync. `delta`/`thinking` accumulate text
// only while their phase is uninterrupted; any tool call (or reset) starts the next phase fresh.
const streamingActivityReducer = (state: StreamingActivity, event: StreamingActivityEvent): StreamingActivity => {
  switch (event.type) {
    case 'reset':
    case 'toolResult':
      return { kind: 'idle' };
    case 'delta':
      return { kind: 'answer', text: (state.kind === 'answer' ? state.text : '') + event.text };
    case 'thinking':
      return { kind: 'thinking', text: (state.kind === 'thinking' ? state.text : '') + event.text };
    case 'toolCall':
      return { kind: 'tool', label: event.label };
  }
};

export {
  toolLabel,
  newConversationScope,
  filterConversations,
  formatLastEdited,
  INITIAL_STREAMING_ACTIVITY,
  streamingActivityReducer,
};
